/**
 * Reusable hooks for table column state — visibility (which
 * columns to show) and widths (per-column override).
 *
 * Each is keyed by `storageKey` so individual tables (Endpoint
 * Inventory, User Management, Provisioning Tokens, future Trace
 * listing, …) get their own remembered preferences in localStorage.
 *
 * `useDataTableColumns` at the bottom is the canonical entry point
 * — it composes visibility + widths + the derived gridTemplateColumns
 * string + ColumnPicker props into one object that callers pass into
 * `<DataTable cols={…} />`.
 */
import { useCallback, useMemo, useState } from "react";
import { browserStorageKey } from "./browserStorageScope.js";

const STORAGE_PREFIX = "dashboard:columns:";

// ── Visibility ───────────────────────────────────────────────────

function loadVisible(storageKey) {
    if (!storageKey) return null;
    try {
        const key = browserStorageKey(STORAGE_PREFIX + storageKey + ":visible");
        localStorage.removeItem(STORAGE_PREFIX + storageKey + ":visible");
        if (!key) return null;
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? new Set(parsed) : null;
    } catch {
        return null;
    }
}

function saveVisible(storageKey, idSet) {
    if (!storageKey) return;
    try {
        const k = browserStorageKey(STORAGE_PREFIX + storageKey + ":visible");
        if (!k) return;
        if (idSet == null) localStorage.removeItem(k);
        else localStorage.setItem(k, JSON.stringify([...idSet]));
    } catch {}
}

export function useColumnSelection({ storageKey, columns, defaultVisible }) {
    const defaultSet = useMemo(() => new Set(defaultVisible), [defaultVisible]);
    const [override, _setOverride] = useState(() => loadVisible(storageKey));

    const setOverride = useCallback((next) => {
        _setOverride(next);
        saveVisible(storageKey, next);
    }, [storageKey]);

    const visibleIds = override ?? defaultSet;

    const visibleColumns = useMemo(
        () => columns.filter((c) => visibleIds.has(c.id)),
        [columns, visibleIds],
    );

    const toggle = useCallback((id) => {
        const base = override ?? defaultSet;
        const next = new Set(base);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setOverride(next);
    }, [override, defaultSet, setOverride]);

    const setVisible = useCallback((idSet) => {
        setOverride(new Set(idSet));
    }, [setOverride]);

    const resetToDefault = useCallback(() => {
        setOverride(null);
    }, [setOverride]);

    return {
        visibleIds, visibleColumns,
        toggle, setVisible, resetToDefault,
        isOverridden: override != null,
    };
}

// ── Widths ───────────────────────────────────────────────────────

const MIN_WIDTH = 50;
const MAX_WIDTH = 1000;

function loadWidths(storageKey) {
    if (!storageKey) return null;
    try {
        const key = browserStorageKey(STORAGE_PREFIX + storageKey + ":widths");
        localStorage.removeItem(STORAGE_PREFIX + storageKey + ":widths");
        if (!key) return null;
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function saveWidths(storageKey, map) {
    if (!storageKey) return;
    try {
        const k = browserStorageKey(STORAGE_PREFIX + storageKey + ":widths");
        if (!k) return;
        if (!map || Object.keys(map).length === 0) localStorage.removeItem(k);
        else localStorage.setItem(k, JSON.stringify(map));
    } catch {}
}

function clampWidth(w) {
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(w)));
}

export function useColumnWidths({ storageKey, columns }) {
    const [overrides, _setOverrides] = useState(() => loadWidths(storageKey) ?? {});

    const setOverrides = useCallback((nextOrFn) => {
        _setOverrides((prev) => {
            const next = typeof nextOrFn === "function" ? nextOrFn(prev) : nextOrFn;
            saveWidths(storageKey, next);
            return next;
        });
    }, [storageKey]);

    const defaultWidth = useCallback((id) => {
        const c = columns.find((x) => x.id === id);
        return c?.width ?? 140;
    }, [columns]);

    const widthFor = useCallback((id) => {
        return overrides[id] != null ? overrides[id] : defaultWidth(id);
    }, [overrides, defaultWidth]);

    const setWidth = useCallback((id, w) => {
        setOverrides((prev) => ({ ...prev, [id]: clampWidth(w) }));
    }, [setOverrides]);

    const resetToDefault = useCallback(() => {
        setOverrides({});
    }, [setOverrides]);

    return {
        widthFor, setWidth, resetToDefault,
        isOverridden: Object.keys(overrides).length > 0,
    };
}

// ── Fitting the width ────────────────────────────────────────────
//
// A table's columns as CSS grid tracks: each takes its pixel width or
// stretches as `minmax(0,1fr)` when it's marked `flex: true`. With no flex
// column a trailing `minmax(0,1fr)` spacer absorbs the slack.
export function gridColsOf(columns) {
    const tracks = columns.map((c) => (c.flex ? "minmax(0,1fr)" : `${c.width}px`));
    return columns.some((c) => c.flex) ? tracks.join(" ") : `${tracks.join(" ")} minmax(0,1fr)`;
}

// The width a row of these columns needs: fixed widths, a flex column's
// `minWidth` (120 by default), DataTable's 8px gaps and 6px row padding.
export const FLEX_MIN_WIDTH = 120;
export function columnsWidth(columns) {
    if (!columns.length) return 0;
    return columns.reduce((sum, c) => sum + (c.flex ? c.minWidth ?? FLEX_MIN_WIDTH : c.width), 0)
        + 8 * (columns.length - 1) + 12;
}

// The columns that fit `room` pixels. A column marked `fold` (a number,
// 1 first) gives way, lowest first, until the rest fit, so a narrow table
// drops what matters least instead of scrolling sideways. Columns without
// `fold` always stay. Unmeasured (`room` null) keeps every column.
export function foldColumns(columns, room) {
    if (room == null || !columns.some((c) => c.fold != null)) return columns;
    let shown = columns;
    while (columnsWidth(shown) > room) {
        const next = shown
            .filter((c) => c.fold != null)
            .reduce((low, c) => (low == null || c.fold < low.fold ? c : low), null);
        if (!next) break;
        shown = shown.filter((c) => c !== next);
    }
    return shown;
}

// ── Composed entry point ─────────────────────────────────────────
//
// `useDataTableColumns` wires visibility + widths together and produces
// the derived state DataTable needs (effective columns, gridCols string,
// ColumnPicker props). One call per table; pass the result to
// `<DataTable cols={…} />` and, if you want a picker, drop
// `<ColumnPicker {...cols.pickerProps} />` wherever it belongs (usually
// AdminViewHeader's `actions` slot).
//
// `storageKey` is optional. Without it everything still works
// in-memory — useful for embedded tables that don't deserve their own
// localStorage namespace.

export function useDataTableColumns({ storageKey, columns, defaultVisible }) {
    const selection = useColumnSelection({ storageKey, columns, defaultVisible });
    const widths = useColumnWidths({ storageKey, columns });

    const effective = useMemo(
        () => selection.visibleColumns.map((c) => ({ ...c, width: widths.widthFor(c.id) })),
        [selection.visibleColumns, widths],
    );

    // Each column either takes its pixel width or stretches as
    // `minmax(0,1fr)` when it's marked `flex: true` (e.g. the trace
    // log's request-summary column).
    //
    // When no column is flex, append a trailing `minmax(0,1fr)`
    // spacer track. The spacer absorbs slack so the table always
    // fills the viewport, and collapses to 0 when columns overflow
    // so horizontal scroll works without fighting any data column's
    // stretch. EndpointInventoryPage's original implementation
    // depended on this behaviour and the resize handles still do.
    const gridCols = useMemo(() => gridColsOf(effective), [effective]);

    const resetAll = useCallback(() => {
        selection.resetToDefault();
        widths.resetToDefault();
    }, [selection, widths]);

    const pickerProps = useMemo(
        () => ({
            columns,
            visibleIds: selection.visibleIds,
            onToggle: selection.toggle,
            onSetVisible: selection.setVisible,
            onResetToDefault: resetAll,
            isOverridden: selection.isOverridden || widths.isOverridden,
        }),
        [columns, selection, widths, resetAll],
    );

    return { columns, effective, gridCols, selection, widths, pickerProps };
}
